import React, { startTransition, useState } from 'react';
import { Stack, Dialog } from '@mui/material';
import { LanguageSuggestionBannerDialog as classes } from './styleObjects/LanguageSuggestionBannerDialog';
import { setCookie } from '@services/cookieService';
import { CloseIcon } from '@icon/CloseIcon';
import { LanguageRegion } from '@icon/LanguageRegion';
import { languageBannerTranslations } from '@helpers/languageSuggestionBannerHelper';
import { useAtom, useSetAtom } from 'jotai';
import { Button, IconButton, Text } from '@web-for-marketing/react-ui';
import { isRegionSelectionDialogOpenAtom, selectedLanguageAtom } from '@atoms/appSettings';
import { CookieKey } from '@models/cookie';
import { useLanguageBanner } from '@queries/languageSuggestion';
import { LanguageCode } from '@models/languageCode';

const defaultTranslation = languageBannerTranslations[LanguageCode.en];

export default function LanguageSuggestionBanner(): React.ReactElement {
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const { data: languageBanner } = useLanguageBanner();
    const [enableDialog, setEnableDialog] = useState(true);
    const open = !!(enableDialog && languageBanner?.showBanner);
    const relativeUrl = languageBanner?.relativeUrl ?? '';
    const primaryTranslation = languageBanner ? languageBannerTranslations[languageBanner.acceptLanguage] : undefined;
    const stayTranslation = languageBannerTranslations[selectedLanguage];
    const translation = primaryTranslation ?? defaultTranslation;
    const bannerText = translation.pageText;
    const redirectButtonText = translation.redirectButtonText;
    const stayedirectButtonText = stayTranslation
        ? stayTranslation.stayRedirectButtonText
        : defaultTranslation.stayRedirectButtonText;
    const languageRegion = translation.languageRegion;
    const chooseBannerText = translation.chooseBannerText;
    const setIsRegionSelectionDialogOpen = useSetAtom(isRegionSelectionDialogOpenAtom);

    const setCookieAndCloseDialog = (): void => {
        setCookie(CookieKey.LanguageSuggestionBanner, true, 1);
        setEnableDialog(false);
    };

    const handleRegionSelectorOpen = (): void => {
        setCookieAndCloseDialog();
        startTransition(() => {
            setIsRegionSelectionDialogOpen(true);
        });
    };

    return (
        <Dialog
            onClose={setCookieAndCloseDialog}
            open={open}
            fullWidth
            maxWidth='md'
            css={classes.modalDialog}
            aria-labelledby='language-suggestion-title'
        >
            <Stack alignItems='center' css={classes.modelContainer}>
                <IconButton
                    label='close'
                    onClick={setCookieAndCloseDialog}
                    size='small'
                    shape='round'
                    variant='ghost'
                    css={classes.closeIcon}
                >
                    <CloseIcon />
                </IconButton>
                <div css={classes.regionIcon}>
                    <LanguageRegion />
                </div>
                <div css={classes.descriptionContainer}>
                    <Text variant='body1' id='language-suggestion-title'>
                        {bannerText} {languageRegion}
                    </Text>
                </div>
                <div css={classes.regionButtonContainer}>
                    <Button component='a' href={relativeUrl} css={classes.languageButton}>
                        {redirectButtonText}
                    </Button>
                    <Button variant='secondary' css={classes.stayLanguageButton} onClick={setCookieAndCloseDialog}>
                        {stayedirectButtonText}
                    </Button>
                </div>
                <div css={classes.chooseLanguage}>
                    <Button variant='ghost' onClick={handleRegionSelectorOpen}>
                        {chooseBannerText}
                    </Button>
                </div>
            </Stack>
        </Dialog>
    );
}
